import { apiRequest } from './api';
import { loadLive } from './data';
import { applyTranslations, getLocale, pluralize, setLocale, t, type Locale } from './i18n';
import { setUser, state } from './state';
import type { LiveStream, SessionUser } from './types';
import { $, h, link, svg } from './ui/dom';
import { icons, SOCIALS, type IconName } from './ui/icons';
import { setupOverlays } from './ui/overlay';

const COLLAPSE_KEY = 'gaz.sidebar.collapsed';
const LIVE_POLL_MS = 90_000;

export function fillIcons(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-icon]').forEach((el) => {
    const name = el.dataset.icon as IconName;
    if (!icons[name] || el.dataset.iconDone) return;
    el.classList.add('icon');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = icons[name];
    el.dataset.iconDone = '1';
  });
}

function setupSidebar(): void {
  const body = document.body;
  let collapsed = false;
  try {
    collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    /* ignore */
  }

  const toggle = $('sb-toggle');
  const applyCollapsed = (): void => {
    body.classList.toggle('sb-collapsed', collapsed);
    const key = collapsed ? 'nav.expand' : 'nav.collapse';
    toggle?.setAttribute('data-i18n-title', key);
    toggle?.setAttribute('data-i18n-aria-label', key);
    toggle?.setAttribute('title', t(key));
    toggle?.setAttribute('aria-label', t(key));
    // Labels become tooltips when collapsed.
    document.querySelectorAll<HTMLElement>('.sb-link').forEach((a) => {
      const label = a.querySelector('.sb-label')?.textContent ?? '';
      if (collapsed) a.setAttribute('title', label);
      else a.removeAttribute('title');
    });
  };
  applyCollapsed();

  toggle?.addEventListener('click', () => {
    if (window.matchMedia('(max-width: 900px)').matches) {
      body.classList.remove('sb-open');
      return;
    }
    collapsed = !collapsed;
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
    applyCollapsed();
  });
  document.addEventListener('i18n:changed', applyCollapsed);

  $('menu-open')?.addEventListener('click', () => body.classList.add('sb-open'));
  $('sb-backdrop')?.addEventListener('click', () => body.classList.remove('sb-open'));
  document.addEventListener('route:changed', () => body.classList.remove('sb-open'));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') body.classList.remove('sb-open');
  });
}

function setupLanguage(): void {
  const buttons = document.querySelectorAll<HTMLButtonElement>('[data-locale]');
  const sync = (): void => {
    buttons.forEach((b) => b.classList.toggle('active', b.dataset.locale === getLocale()));
  };
  buttons.forEach((b) => b.addEventListener('click', () => setLocale(b.dataset.locale as Locale)));
  document.addEventListener('i18n:changed', sync);
  document.documentElement.lang = getLocale();
  sync();
}

function renderSocials(): void {
  const side = $('sb-socials');
  side?.replaceChildren(
    ...SOCIALS.map((s) => link(s.url, { class: 'sb-social', attrs: { 'aria-label': s.label, title: s.label } }, svg(icons[s.key]))),
  );
  const foot = $('footer-socials');
  foot?.replaceChildren(...SOCIALS.map((s) => h('li', null, link(s.url, 'footer-social', svg(icons[s.key]), s.label))));
  const year = $('footer-year');
  if (year) year.textContent = String(new Date().getFullYear());
}

// ───────── Admin link / session ─────────

let rolesPromise: Promise<void> | null = null;

/** Lazily loads isAdmin into state.user. */
export function ensureRolesLoaded(force = false): Promise<void> {
  if (!state.token) return Promise.resolve();
  if (!force && state.user?.isAdmin !== undefined) return Promise.resolve();
  if (rolesPromise) return rolesPromise;
  rolesPromise = apiRequest<{ user: SessionUser }>('/api/auth/me', { auth: true })
    .then(({ user }) => setUser(user))
    .catch(() => undefined)
    .finally(() => {
      rolesPromise = null;
    });
  return rolesPromise;
}

export function renderUserBox(): void {
  $('sb-admin')?.classList.toggle('hidden', !state.user?.isAdmin);
}

// ───────── Live on Twitch ─────────

let liveTimer: number | undefined;
let lastLive: LiveStream[] = [];

function renderLive(): void {
  const box = $('live-box');
  const top = $('topbar-live');
  if (!box || !top) return;
  const streams = lastLive;
  box.classList.toggle('hidden', streams.length === 0);
  top.classList.toggle('hidden', streams.length === 0);
  if (!streams.length) {
    box.replaceChildren();
    top.replaceChildren();
    return;
  }

  box.replaceChildren(
    h('div', 'live-head', h('span', 'live-dot'), h('span', { class: 'live-title', text: t('live.title') }), h('span', 'live-count', String(streams.length))),
    ...streams.slice(0, 4).map((s) =>
      link(
        s.url,
        { class: 'live-item', attrs: { title: `${s.name} — ${s.title}` } },
        h('img', { attrs: { src: s.thumbnail, alt: '', loading: 'lazy', width: 440, height: 248 } }),
        h(
          'span',
          'live-meta',
          h('b', { text: s.is_clan ? `${s.name} · ${t('live.clan')}` : s.name }),
          h('small', { text: s.title }),
          h('small', 'live-viewers', svg(icons.twitch), `${s.viewers} ${pluralize(s.viewers, t('live.viewers'))}`),
        ),
      ),
    ),
  );

  const first = streams[0];
  if (first) {
    top.replaceChildren(
      link(first.url, { class: 'topbar-live-link', attrs: { 'aria-label': `${t('live.title')}: ${first.name}` } }, h('span', 'live-dot'), 'LIVE'),
    );
  }
}

async function pollLive(): Promise<void> {
  try {
    lastLive = await loadLive();
  } catch {
    lastLive = [];
  }
  renderLive();
  document.dispatchEvent(new CustomEvent('live:changed', { detail: lastLive }));
}

export function getLiveStreams(): LiveStream[] {
  return lastLive;
}

function setupLive(): void {
  const schedule = (): void => {
    window.clearInterval(liveTimer);
    liveTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void pollLive();
    }, LIVE_POLL_MS);
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void pollLive();
  });
  document.addEventListener('i18n:changed', renderLive);
  void pollLive();
  schedule();
}

export function setupApp(): void {
  applyTranslations(document);
  fillIcons();
  setupSidebar();
  setupLanguage();
  renderSocials();
  setupOverlays();
  setupLive();

  renderUserBox();
  document.addEventListener('session:changed', renderUserBox);
  void ensureRolesLoaded();
}
